import { usePlatformFlag, usePlatformFlagsStore } from '@/config/platformFlags';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEffect } from 'react';

/**
 * Which sidebar this tenant gets, without the flash.
 *
 * `/me/platform-flags` resolves a beat after the shell mounts, and the
 * pre-auth fallback is `false` — so an opted-in clinic would paint the old
 * 28-row sidebar and then collapse it to 11 under the cursor. That reads as a
 * bug even though it is correct.
 *
 * So the last resolved answer is remembered per tenant and used for the first
 * paint. Per tenant, not globally, because a user with memberships in two
 * clinics would otherwise get the wrong shape for a frame after switching.
 *
 * Deliberately NOT a general flag cache. Most flags here are kill switches
 * (SIGNUPS_ENABLED, the bot channel toggles) where a stale "on" for one paint
 * is a real problem. This one is presentation-only in both directions, which
 * is exactly what makes caching it safe.
 *
 * `resolved` is false only on a tenant's very first load, before any answer
 * has been cached. Callers should hold rather than guess: an empty nav for a
 * moment beats the wrong nav for a moment.
 */

const KEY = (tenantId: string) => `maie-nav-diet:${tenantId}`;

const readCached = (tenantId: string | null): boolean | null => {
  if (!tenantId) return null;
  try {
    const raw = localStorage.getItem(KEY(tenantId));
    return raw === null ? null : raw === '1';
  } catch {
    return null;
  }
};

const writeCached = (tenantId: string | null, value: boolean) => {
  if (!tenantId) return;
  try {
    localStorage.setItem(KEY(tenantId), value ? '1' : '0');
  } catch {
    /* private mode or quota — we just pay the flash next time */
  }
};

export interface NavDietState {
  navDiet: boolean;
  /** False only before this tenant has ever had an answer cached. */
  resolved: boolean;
}

export function useNavDiet(): NavDietState {
  const flagValue = usePlatformFlag('NAV_DIET_ENABLED');
  const flagsLoaded = usePlatformFlagsStore((s) => s.loaded);
  const activeTenantId = useAuthStore((s) => s.activeTenantId);

  useEffect(() => {
    if (flagsLoaded && activeTenantId) writeCached(activeTenantId, flagValue);
  }, [flagsLoaded, activeTenantId, flagValue]);

  if (flagsLoaded) return { navDiet: flagValue, resolved: true };

  const cached = readCached(activeTenantId);
  return cached === null
    ? { navDiet: false, resolved: false }
    : { navDiet: cached, resolved: true };
}
